<template>
  <b-form @submit.prevent="handleSave" class="door-dimensioner">
    <div v-if="showPresets && !isCommercial">
      <slot name="presets-label">
        <p>{{ presetsLabel }}</p>
      </slot>
      <div class="d-flex justify-content-center">
        <b-card
          v-for="preset of presets"
          :key="preset.name"
          no-body
          class="linked-card size-card"
          :class="{
            active: dimensions.width.ft === preset.width && dimensions.height.ft === preset.height
          }"
        >
          <img
            :src="preset.image"
          />
          <a
            @click.prevent="
              dimensions.width.ft = preset.width;
              dimensions.height.ft = preset.height;
            "
            class="card-link"
          ></a>
          <b-card-footer
            class="size-footer"
          >
            {{ preset.name }}
            <span>{{ preset.width }}' x {{ preset.height }}'</span>
          </b-card-footer>
        </b-card>
      </div>
    </div>
    <div>
      <slot name="dimensions-label">
        <p v-if="!isCommercial">
          <em>Commercial door sizes can vary greatly.</em>
        </p>
        <p>
          <span>{{ isCommercial ? 'Enter' : 'Or enter' }} requested door size:</span>
          <a v-b-popover="popoverConfig" class="info-button text-accent">
            <i class="fa fa-info-circle" />
          </a>
        </p>
      </slot>
      <div class="form-row">
        <label class="col-3">Width</label>
        <b-col>
          <v-select
            v-model="dimensions.width.ft"
            label="width-ft"
            :options="widthFeetOptions"
          ></v-select>
        </b-col>
        <span class="col-1">ft.</span>
        <b-col>
          <v-select
            v-model="dimensions.width.in"
            label="width-in"
            :options="widthInchOptions"
          ></v-select>
        </b-col>
        <span class="col-1">in.</span>
      </div>
      <div class="form-row">
        <label class="col-3">Height</label>
        <b-col>
          <v-select
            v-model="dimensions.height.ft"
            label="height-ft"
            :options="heightFeetOptions"
            >
          </v-select>
        </b-col>
        <span class="col-1">ft.</span>
        <b-col>
          <v-select
            v-model="dimensions.height.in"
            label="height-in"
            :options="heightInchOptions"
          ></v-select>
        </b-col>
        <span class="col-1">in.</span>
      </div>
    </div>
    <footer class="my-3">
      <div v-if="showEditControls" class="form-row">
        <b-card
          v-for="shapePreset of shapePresets"
          :key="shapePreset.name"
          no-body
          class="linked-card size-card type-card"
          :class="{
            active: dimensions.doorShape === shapePreset.doorShape
          }"
        >
          <img
            :src="shapePreset.image"
          />
          <a
            @click.prevent="
              dimensions.doorShape = shapePreset.doorShape;
            "
            class="card-link"
          >
          </a>
          <b-card-footer
            class="size-footer"
          >
            {{ shapePreset.name }}
          </b-card-footer>
        </b-card>
        <b-col>
          <b-btn @click="handleRemove" block>{{ deleteLabel }}</b-btn>
        </b-col>
        <b-col>
          <b-btn @click="handleDuplicate" block>{{ duplicateLabel }}</b-btn>
        </b-col>
        <b-col>
          <b-btn @click="handleUpdate" type="submit" block variant="primary">
            {{ updateLabel }}
          </b-btn>
        </b-col>
      </div>
      <div v-else class="text-center">
        <b-btn
          type="submit"
          variant="primary"
          :disabled="!dimensions.width.ft || !dimensions.height.ft"
        >
          {{ createLabel }}
        </b-btn>
      </div>
      <transition name="fade">
        <div v-if="message" class="alert alert-success">{{ message }}</div>
      </transition>
    </footer>

    <slot />
  </b-form>
</template>

<script>
import Vue from 'vue';
import MaintainAspectRatio from '@/components/utility/MaintainAspectRatio';
import { packDimensions, unpackDimensions, doorShapes } from '@/helpers/dimensions';
import { mapGetters } from 'vuex';
import 'vue-select/dist/vue-select.css';
import vSelect from 'vue-select';
import range from 'lodash.range';

Vue.component('v-select', vSelect);

export default {
  name: 'door-dimensioner',
  props: {
    layer: Object,
    showPresets: Boolean,
    presetsLabel: {
      type: String,
      default: 'Select a typical residential door size:',
    },
    dimensionsLabel: {
      type: String,
      default: 'Enter your garage door opening size, then click <strong>ADD NEW DOOR</strong>:',
    },
    createLabel: {
      type: String,
      default: 'Add New Door',
    },
    duplicateLabel: {
      type: String,
      default: 'Duplicate',
    },
    updateLabel: {
      type: String,
      default: 'Update',
    },
    deleteLabel: {
      type: String,
      default: 'Remove',
    },
    showEditControls: Boolean,
    showTypeControls: Boolean,
    resetOnSave: Boolean,
  },
  data() {
    return {
      dimensions: undefined,
      presets: [
        {
          name: 'Single Door',
          width: 8,
          height: 7,
          image: '/static/images/singleIcon.png',
        },
        {
          name: 'Double Door',
          width: 16,
          height: 7,
          image: '/static/images/doubleIcon.png',
        },
      ],
      shapePresets: [
        doorShapes.rectangular,
        doorShapes.angled,
        doorShapes.arched,
      ],

      popoverConfig: {
        trigger: 'hover',
        placement: 'top',
        content: `
          Most single car doors range from 8ft to 10ft wide.
          Most double car doors range from 16ft to 18ft wide.

          Typical 7ft high doors will have 4 sections.
          Typical 8ft high doors will have 5 sections.
          Commercial dock door dimensions are typically 8ft in width, with 8ft, 9ft, or 10ft heights.
        `,
      },
      widthFeetOptions: range(6, 31),
      widthInchOptions: range(0, 12, 2),
      heightFeetOptions: range(6, 21),
      heightInchOptions: range(0, 12, 3),
      message: undefined,
      validateMessage: 'Width and Height must be at least 3 feet',
    };
  },
  watch: {
    layer: {
      handler(layer) {
        this.dimensions = unpackDimensions(layer);
      },
      immediate: true,
      deep: true,
    },
  },
  methods: {
    // at some low dimensions, CHIPattern returns a no pattern error
    // 3 feet seems to be the minimum, but this may be product dependent
    // @TODO: try to call CHIPattern to see where the limits are per product
    validateDimensions(dimensions) {
      if ((dimensions.width && dimensions.width.ft < 3) ||
        (dimensions.height && dimensions.height.ft < 3)) {
        this.$root.alert(this.validateMessage, 'Dimensions');
        return false;
      }
      return true;
    },
    handleSave() {
      if (this.validateDimensions(this.dimensions)) {
        // send extended layer
        this.$emit('save', this.layer, packDimensions(this.dimensions));

        // reset fields
        if (this.resetOnSave) {
          this.dimensions = unpackDimensions();
        }
      }
    },
    handleUpdate() {
      if (this.validateDimensions(this.dimensions)) {
        // alert user
        this.message = 'Door size updated';
        setTimeout(() => {
          this.message = null;
        }, 1500);
      }
    },
    handleDuplicate() {
      this.$emit('duplicate', this.layer, packDimensions(this.dimensions));
    },
    handleRemove() {
      this.$emit('remove', this.layer);
    },
  },
  components: {
    MaintainAspectRatio,
  },
  computed: {
    ...mapGetters([
      'isCommercial',
    ]),
  },
};
</script>

<style lang="scss">
@import "~@/assets/styles/variables.chi.scss";

.door-dimensioner {
  .maintain-aspect-ratio {
    height: 100%;

    > * {
      white-space: nowrap;
    }
  }

  .form-row {
    align-items: center;
    margin-top: 8px;
    margin-bottom: 8px;

    label {
      margin-bottom: 0;
    }
  }

  .size-card {
    flex-direction: column;
    border: 0 !important;

    .size-footer {
      background-color: white;
      border: 0;

      span {
        white-space: nowrap;
      }
    }

    &.type-card {
      width: calc(33% - #{$spacer});  // type cards have three divs, so 33%
    }
  }

}
</style>



// WEBPACK FOOTER //
// src/components/DoorDimensioner.vue